import { BOARD, GROUPS, LEVEL_NAMES, MAX_LEVEL, groupTiles } from '../game/board'
import { canAfford, canUpgrade, dealDown, downPayment, loanMonthly, upgradeCostFor } from '../game/engine'
import { hasEffect, totalExpenses } from '../game/finance'
import { controlsCurrent, useGame } from '../store/gameStore'
import { RaiseCash } from './RaiseCash'
import { TileArt } from './TileArt'
import type { DealCard, GameState, Player, Prompt } from '../game/types'
import { money, short, signed } from '../util/format'

export function PromptModal({ g }: { g: GameState }) {
  const prompt = g.prompt
  const { confirm, buy, pass, takeDeal, passDeal, surrender, upgrade } = useGame()
  const mine = useGame(controlsCurrent)
  if (!prompt) return null
  const p = g.players[g.current]

  return (
    <div className="modal-scrim">
      <div className={`modal ${modalTone(prompt)}`} role="dialog" aria-modal="true">
        {body(prompt, g, p)}
        <div className="modal-actions">
          {mine ? (
            actions(prompt, g, p, { confirm, buy, pass, takeDeal, passDeal, surrender, upgrade })
          ) : (
            <span className="faint bot-thinking">
              {p.character.emoji} {p.name} {p.isBot ? 'กำลังคิด…' : 'กำลังตัดสินใจ…'}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function modalTone(prompt: Prompt): string {
  switch (prompt.t) {
    case 'event':
      return prompt.card.tone === 'good' ? 'tone-good' : prompt.card.tone === 'bad' ? 'tone-bad' : 'tone-weird'
    case 'payRent':
    case 'tax':
    case 'bankruptWarning':
      return 'tone-bad'
    case 'jackpot':
    case 'freedom':
      return 'tone-good'
    case 'payday':
      return prompt.amount >= 0 ? 'tone-good' : 'tone-bad'
    default:
      return ''
  }
}

/* ------------------------------------------------------------------ */

function body(prompt: Prompt, g: GameState, p: Player) {
  switch (prompt.t) {
    case 'buyProperty': {
      const tile = BOARD[prompt.tileId]
      const price = tile.price ?? 0
      const down = downPayment(p, price)
      const loan = price - down
      const monthly = tile.monthly?.[0] ?? 0
      const loanPay = loanMonthly(loan)
      const group = tile.group ? GROUPS[tile.group] : null
      return (
        <>
          <Head
            emoji={tile.emoji}
            icon={tile.icon}
            kicker={group?.name ?? (tile.kind === 'transport' ? 'ท่าขนส่ง' : 'สาธารณูปโภค')}
            title={tile.name}
          />
          <p className="modal-text">
            ว่างอยู่! ราคา <b className="num">{money(price)}</b>
            {tile.note ? ` — ${tile.note}` : ''}
          </p>
          <div className="opt-grid">
            <div className="opt">
              <span className="opt-title">💵 ซื้อสด</span>
              <span className="opt-line">
                จ่ายทันที <b className="num bad-text">{money(price)}</b>
              </span>
              <span className="opt-line">
                กระแสเงินสด <b className="num good-text">{signed(monthly)}/เดือน</b>
              </span>
              <span className="faint tiny">ไม่มีหนี้ผูกพัน เป้าหมายหลุดวงจรหนูไม่ขยับ</span>
            </div>
            <div className="opt">
              <span className="opt-title">🏦 ผ่อน (เลเวอเรจ)</span>
              <span className="opt-line">
                ดาวน์ <b className="num bad-text">{money(down)}</b> · กู้ {short(loan)}
              </span>
              <span className="opt-line">
                กระแสเงินสด{' '}
                <b className={`num ${monthly - loanPay >= 0 ? 'good-text' : 'bad-text'}`}>
                  {signed(monthly - loanPay)}/เดือน
                </b>
              </span>
              <span className="faint tiny">
                ค่าผ่อน {money(loanPay)}/เดือน เข้าไปในรายจ่าย → เป้าหมายขยับขึ้นเป็น{' '}
                {money(totalExpenses(p) + loanPay)}
              </span>
            </div>
          </div>
          {tile.rent && tile.kind === 'property' && (
            <div className="rent-table">
              {tile.rent.map((r, i) => (
                <span key={i}>
                  <em className="faint tiny">{LEVEL_NAMES[i].replace('อัปเกรด ', 'Lv')}</em>
                  <b className="num">{short(r)}</b>
                </span>
              ))}
            </div>
          )}
        </>
      )
    }

    case 'payRent': {
      const tile = BOARD[prompt.tileId]
      const owner = g.players[prompt.toPlayer]
      return (
        <>
          <Head emoji="💸" icon={tile.icon} kicker="ค่าเช่า" title={tile.name} />
          <p className="modal-text">
            ที่นี่เป็นของ <b style={{ color: owner.character.color }}>{owner.character.emoji} {owner.name}</b> แล้ว
            — จ่ายค่าเช่า
          </p>
          <div className="big-amount bad-text num">{money(prompt.amount)}</div>
          <p className="faint tiny center">เงินสดคุณจะเหลือ {money(p.cash - prompt.amount)}</p>
        </>
      )
    }

    case 'deal': {
      const list = prompt.choices ?? [prompt.card]
      const locked = hasEffect(g, p, 'noBuy')
      return (
        <>
          <Head emoji="💡" kicker="โอกาสธุรกิจ" title={list.length > 1 ? 'เลือกดีลหนึ่งใบ' : list[0].name} />
          {locked && <div className="shield-note">🔓 บัญชีคุณยังถูกแฮกอยู่ — ปิดดีลไม่ได้ในรอบนี้</div>}
          <div className={`deal-grid${list.length > 1 ? ' two' : ''}`}>
            {list.map((c) => (
              <DealView key={c.id} card={c} p={p} single={list.length === 1} locked={locked} />
            ))}
          </div>
        </>
      )
    }

    case 'event': {
      const c = prompt.card
      return (
        <>
          <Head
            emoji={c.emoji}
            kicker={c.scope === 'global' ? '🌍 เหตุการณ์ระดับโลก — กระทบทุกคน' : '🎯 เหตุการณ์เฉพาะตัว'}
            title={c.title}
          />
          <p className="modal-text">{c.text}</p>
          {c.scope === 'personal' && c.tone === 'bad' && p.shield > 0 && (
            <div className="shield-note">🛡️ โล่พลังงานของคุณจะดูดซับโทษนี้ไว้!</div>
          )}
        </>
      )
    }

    case 'tax': {
      const tile = BOARD[prompt.tileId]
      return (
        <>
          <Head emoji={tile.emoji} icon={tile.icon} kicker="สรรพากรจักรวาล" title={tile.name} />
          <p className="modal-text">
            คิดจากสินทรัพย์รวมของคุณ อัตรา {Math.round((tile.taxRate ?? 0) * 100)}% — เงินเข้ากองกลาง
          </p>
          <div className="big-amount bad-text num">{money(prompt.amount)}</div>
        </>
      )
    }

    case 'jackpot':
      return (
        <>
          <Head emoji="🏝️" kicker="ท่าอวกาศพักผ่อน" title={prompt.amount > 0 ? 'เก็บกองกลาง!' : 'กองกลางว่างเปล่า'} />
          {prompt.amount > 0 ? (
            <div className="big-amount good-text num">{signed(prompt.amount)}</div>
          ) : (
            <p className="modal-text">ยังไม่มีใครจ่ายภาษีเข้ากองกลางเลย — พักผ่อนเฉย ๆ ก็ดี</p>
          )}
        </>
      )

    case 'payday': {
      const d = prompt.detail
      return (
        <>
          <Head emoji="💵" kicker={`รอบที่ ${g.round}`} title="Payday!" />
          <div className="payday-rows">
            <span>
              เงินเดือน <b className="num good-text">{money(d.salary)}</b>
            </span>
            <span>
              Passive Income <b className="num good-text">{money(d.passive)}</b>
            </span>
            <span>
              รายจ่ายประจำ <b className="num bad-text">{money(-d.expenses)}</b>
            </span>
            <span>
              ค่าผ่อนหนี้ <b className="num bad-text">{money(-d.loans)}</b>
            </span>
          </div>
          <div className={`big-amount num ${d.net >= 0 ? 'good-text' : 'bad-text'}`}>{signed(d.net)}</div>
          {d.passive > 0 && (
            <p className="faint tiny center">
              Passive Income คิดเป็น {Math.round((d.passive / Math.max(1, d.expenses + d.loans)) * 100)}% ของรายจ่าย
            </p>
          )}
        </>
      )
    }

    case 'bankruptWarning':
      return (
        <>
          <Head emoji="🩸" kicker="เงินสดติดลบ" title="ต้องระดมเงินด่วน!" />
          <p className="modal-text">
            เลือกขายหรือจำนองจนเงินสดกลับมาไม่ติดลบ — ตัวเลขคือเงินที่ได้จริงหลังหักหนี้ที่ผูกอยู่
          </p>
          <RaiseCash g={g} p={p} />
        </>
      )

    case 'freedom': {
      const w = g.players[prompt.playerId]
      return (
        <>
          <Head emoji="🏆" kicker="หลุดพ้นวงจรหนู" title={`${w.name} ชนะ!`} />
          <p className="modal-text">
            {w.character.emoji} {w.name} ทำ Passive Income แซงรายจ่ายรวมได้แล้ว — ไม่ต้องทำงานเพื่อเงินอีก
          </p>
        </>
      )
    }

    case 'ownProperty': {
      const tile = BOARD[prompt.tileId]
      const ts = g.tiles[prompt.tileId]
      const group = tile.group ? GROUPS[tile.group] : null
      const cost = upgradeCostFor(p, tile)
      const maxed = ts.level >= MAX_LEVEL
      const rentNow = tile.rent?.[ts.level] ?? 0
      const rentNext = tile.rent?.[ts.level + 1] ?? 0
      return (
        <>
          <Head
            emoji={tile.emoji}
            icon={tile.icon}
            kicker={group?.name ?? 'ทรัพย์สินของคุณ'}
            title={tile.name}
          />
          <p className="modal-text">
            ระดับตอนนี้ <b>{LEVEL_NAMES[ts.level]}</b>
            {ts.mortgaged && <span className="bad-text"> · ถูกจำนองอยู่</span>}
          </p>
          {!maxed && tile.kind === 'property' && (
            <div className="opt-grid">
              <div className="opt">
                <span className="opt-title">ค่าเช่าตอนนี้</span>
                <span className="opt-line">
                  <b className="num">{money(rentNow)}</b>
                </span>
              </div>
              <div className="opt">
                <span className="opt-title">อัปเกรดแล้วได้</span>
                <span className="opt-line">
                  <b className="num good-text">{money(rentNext)}</b>
                </span>
                <span className="faint tiny">จ่าย {money(cost)}</span>
              </div>
            </div>
          )}
          <p className="faint tiny center">{upgradeHint(g, p, prompt.tileId)}</p>
        </>
      )
    }

    case 'info':
      return (
        <>
          <Head emoji={prompt.emoji} kicker="" title={prompt.title} />
          <p className="modal-text">{prompt.text}</p>
        </>
      )
  }
}

function DealView({
  card,
  p,
  single,
  locked,
}: {
  card: DealCard
  p: Player
  single: boolean
  locked: boolean
}) {
  const { takeDeal } = useGame()
  const down = dealDown(card)
  const roi = down > 0 ? card.cashflow / down : 0
  const affordable = canAfford(p, down) && !locked
  return (
    <div className="deal-card">
      <span className="deal-top">
        <span className="deal-emoji">{card.emoji}</span>
        <span>
          <b>{card.name}</b>
          <span className="faint tiny">
            {card.size === 'big' ? 'ดีลใหญ่' : 'ดีลเล็ก'} · {kindLabel(card.kind)}
          </span>
        </span>
      </span>
      <p className="deal-text">{card.text}</p>
      <div className="deal-stats">
        <span>
          <em className="faint tiny">จ่ายวันนี้</em>
          <b className="num">{money(down)}</b>
        </span>
        {card.down && card.down < card.price && (
          <span>
            <em className="faint tiny">มูลค่าเต็ม</em>
            <b className="num faint">{short(card.price)}</b>
          </span>
        )}
        <span>
          <em className="faint tiny">กระแสเงินสด</em>
          <b className={`num ${card.cashflow > 0 ? 'good-text' : 'faint'}`}>
            {card.cashflow > 0 ? `+${money(card.cashflow)}/ด.` : 'ไม่ปันผล'}
          </b>
        </span>
        <span>
          <em className="faint tiny">ผลตอบแทน/เดือน</em>
          <b className={`num ${roi >= 0.013 ? 'good-text' : roi > 0 ? '' : 'faint'}`}>
            {(roi * 100).toFixed(2)}%
          </b>
        </span>
      </div>
      {!single && (
        <button className="btn btn-primary btn-block" disabled={!affordable} onClick={() => takeDeal(card.id)}>
          {locked ? 'บัญชีถูกล็อก' : affordable ? `ปิดดีลนี้ · ${short(down)}` : 'เงินสดไม่พอ'}
        </button>
      )}
    </div>
  )
}

function kindLabel(k: DealCard['kind']): string {
  return { stock: 'หุ้น/กองทุน', realestate: 'อสังหาริมทรัพย์', business: 'ธุรกิจ', crypto: 'คริปโต', exotic: 'สินทรัพย์พิเศษ' }[k]
}

/**
 * หัวโมดัล — ถ้าส่ง icon มาด้วยจะใช้ภาพวาดของช่องนั้นแทนอีโมจิ
 * อีโมจิยังต้องส่งเสมอเพื่อเป็นทางถอยตอนไฟล์ภาพไม่มี
 */
function Head({ emoji, kicker, title, icon }: { emoji: string; kicker: string; title: string; icon?: string }) {
  return (
    <header className="modal-head">
      {icon ? <TileArt icon={icon} emoji={emoji} alt={title} /> : <span className="modal-emoji">{emoji}</span>}
      {kicker && <span className="modal-kicker">{kicker}</span>}
      <h2>{title}</h2>
    </header>
  )
}

/* ------------------------------------------------------------------ */

interface Handlers {
  confirm: () => void
  upgrade: (tileId: number) => void
  buy: (m: 'cash' | 'loan') => void
  pass: () => void
  takeDeal: (id?: string) => void
  passDeal: () => void
  surrender: () => void
}

function actions(prompt: Prompt, g: GameState, p: Player, h: Handlers) {
  switch (prompt.t) {
    case 'buyProperty': {
      const price = BOARD[prompt.tileId].price ?? 0
      const down = downPayment(p, price)
      return (
        <>
          <button className="btn btn-primary" disabled={!canAfford(p, price)} onClick={() => h.buy('cash')}>
            💵 ซื้อสด {short(price)}
          </button>
          <button className="btn btn-warn" disabled={!canAfford(p, down)} onClick={() => h.buy('loan')}>
            🏦 ผ่อน ดาวน์ {short(down)}
          </button>
          <button className="btn btn-ghost" onClick={h.pass}>
            ไม่ซื้อ
          </button>
        </>
      )
    }
    case 'deal': {
      const list = prompt.choices ?? [prompt.card]
      const single = list.length === 1
      const down = dealDown(list[0])
      const ok = canAfford(p, down) && !hasEffect(g, p, 'noBuy')
      return (
        <>
          {single && (
            <button className="btn btn-primary" disabled={!ok} onClick={() => h.takeDeal()}>
              {ok ? `✅ ปิดดีล · ${short(down)}` : hasEffect(g, p, 'noBuy') ? 'บัญชีถูกล็อก' : 'เงินสดไม่พอ'}
            </button>
          )}
          <button className="btn btn-ghost" onClick={h.passDeal}>
            {single ? 'ปล่อยดีลนี้ไป' : 'ไม่เอาทั้งสองใบ'}
          </button>
        </>
      )
    }
    case 'ownProperty': {
      const tile = BOARD[prompt.tileId]
      const ok = canUpgrade(g, p, prompt.tileId)
      return (
        <>
          {tile.kind === 'property' && (
            <button className="btn btn-primary" disabled={!ok} onClick={() => h.upgrade(prompt.tileId)}>
              🏗️ อัปเกรด {short(upgradeCostFor(p, tile))}
            </button>
          )}
          <button className="btn btn-ghost" onClick={h.confirm}>
            ต่อไป →
          </button>
        </>
      )
    }

    case 'payRent':
      return (
        <button className="btn btn-primary" onClick={h.confirm}>
          จ่ายค่าเช่า {short(prompt.amount)}
        </button>
      )
    case 'tax':
      return (
        <button className="btn btn-primary" onClick={h.confirm}>
          จ่ายภาษี {short(prompt.amount)}
        </button>
      )
    case 'event':
      return (
        <button className="btn btn-primary btn-lg" onClick={h.confirm}>
          รับผลกรรม 🎲
        </button>
      )
    case 'bankruptWarning':
      return (
        <>
          <button className="btn btn-primary" disabled={p.cash < 0} onClick={h.confirm}>
            {p.cash < 0 ? `ยังติดลบ ${short(-p.cash)}` : '✅ เรียบร้อย ไปต่อ'}
          </button>
          {p.cash < 0 && (
            <button
              className="btn btn-danger"
              onClick={() => {
                if (confirm('ยอมแพ้และประกาศล้มละลาย? คุณจะออกจากเกมทันที')) h.surrender()
              }}
            >
              🏳️ ยอมแพ้ ล้มละลาย
            </button>
          )}
        </>
      )
    case 'freedom':
      return <span className="faint">ดูสรุปผลด้านล่าง</span>
    default:
      return (
        <button className="btn btn-primary btn-lg" onClick={h.confirm}>
          เข้าใจแล้ว
        </button>
      )
  }
}


/**
 * บอกเหตุผลที่อัปเกรดไม่ได้ ให้ผู้เล่นรู้ว่าต้องทำอะไรต่อ
 * ไม่งั้นปุ่มจะเทาเฉย ๆ โดยไม่มีใครรู้ว่าติดตรงไหน
 */
function upgradeHint(g: GameState, p: Player, tileId: number): string {
  const tile = BOARD[tileId]
  const ts = g.tiles[tileId]
  if (tile.kind !== 'property') return 'ช่องประเภทนี้อัปเกรดไม่ได้ — รายได้มาจากจำนวนที่ถืออยู่ในหมวดเดียวกัน'
  if (ts.mortgaged) return 'ต้องไถ่ถอนจากการจำนองก่อนถึงจะอัปเกรดได้'
  if (ts.level >= MAX_LEVEL) return 'อัปเกรดถึงระดับสูงสุดแล้ว'

  const group = groupTiles(tile.group!)
  const missing = group.filter((t) => g.tiles[t.id].ownerId !== p.id)
  if (missing.length > 0) {
    return `ต้องถือครบทั้งกลุ่มสีก่อน — ยังขาดอีก ${missing.length} ช่อง (${missing.map((t) => t.name).join(', ')})`
  }
  if (group.some((t) => g.tiles[t.id].mortgaged)) return 'มีช่องในกลุ่มนี้ถูกจำนองอยู่ ต้องไถ่ถอนให้ครบก่อน'

  const minLevel = Math.min(...group.map((t) => g.tiles[t.id].level))
  if (ts.level > minLevel) return 'ต้องอัปเกรดให้กระจายกันในกลุ่ม — ไปอัปช่องที่ระดับต่ำกว่าก่อน'
  if (!canAfford(p, upgradeCostFor(p, tile))) return 'เงินสดไม่พอสำหรับค่าอัปเกรด'
  return 'อัปเกรดแล้วค่าเช่าจะสูงขึ้นทันที'
}
